import React from "react";

// REISOR_DRIVER_NATIVE_SAFE_SELECT_V1
// Android WebView can render native <select>/<option> popups with unreadable contrast.
// This component avoids native <select> entirely: button + custom menu.
export default function NativeSafeSelect({
  value,
  onChange,
  options = [],
  className = "",
  buttonClassName = "",
  menuClassName = "",
  optionClassName = "",
  style,
  disabled = false,
  ariaLabel = "Select",
  placement = "down",
}) {
  const [open, setOpen] = React.useState(false);
  const [openUp, setOpenUp] = React.useState(false);
  const ref = React.useRef(null);

  const items = React.useMemo(
    () =>
      (Array.isArray(options) ? options : []).map((o) =>
        typeof o === "string"
          ? { value: o, label: o, disabled: false }
          : {
              value: o?.value,
              label: o?.label ?? String(o?.value ?? ""),
              disabled: Boolean(o?.disabled),
            }
      ),
    [options]
  );

  const selected =
    items.find((o) => String(o.value) === String(value)) ||
    items.find((o) => !o.disabled) ||
    items[0] ||
    { value: "", label: "" };

  React.useEffect(() => {
    if (!open) return;

    const onDown = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };

    const onKey = (e) => {
      if (e.key === "Escape") setOpen(false);
    };

    document.addEventListener("mousedown", onDown, true);
    document.addEventListener("touchstart", onDown, true);
    document.addEventListener("keydown", onKey, true);

    return () => {
      document.removeEventListener("mousedown", onDown, true);
      document.removeEventListener("touchstart", onDown, true);
      document.removeEventListener("keydown", onKey, true);
    };
  }, [open]);

  const pick = (item) => {
    if (!item || item.disabled || disabled) return;
    onChange?.(item.value);
    setOpen(false);
  };

  const toggleOpen = () => {
    if (disabled) return;

    const next = !open;
    if (next && placement === "auto" && ref.current && typeof window !== "undefined") {
      const rect = ref.current.getBoundingClientRect();
      const desiredHeight = Math.min(Math.max(items.length * 56 + 16, 96), 320);
      const below = Math.max(0, window.innerHeight - rect.bottom - 12);
      const above = Math.max(0, rect.top - 12);
      setOpenUp(below < desiredHeight && above > below);
    }

    setOpen(next);
  };

  return (
    <div ref={ref} className={`relative ${className || ""}`}>
      <button
        type="button"
        disabled={disabled}
        aria-label={ariaLabel}
        aria-haspopup="listbox"
        aria-expanded={open}
        onClick={toggleOpen}
        style={style}
        className={
          buttonClassName ||
          "w-full rounded-2xl border border-slate-700 bg-slate-950 px-4 py-3 text-left text-base font-semibold text-slate-50 outline-none"
        }
      >
        <span className="flex items-center justify-between gap-3">
          <span className="min-w-0 truncate">{selected.label}</span>
          <span className={`shrink-0 text-slate-300 transition ${open ? "rotate-180" : ""}`}>⌄</span>
        </span>
      </button>

      {open ? (
        <div
          role="listbox"
          className={[
            menuClassName ||
              "absolute left-0 right-0 z-[99999] overflow-hidden rounded-2xl border border-slate-700 bg-slate-950 shadow-2xl",
            placement === "auto"
              ? (openUp ? "bottom-full mb-2" : "top-full mt-2")
              : "mt-2",
          ].join(" ")}
        >
          {items.map((item) => {
            const active = String(item.value) === String(value);
            return (
              <button
                key={String(item.value)}
                type="button"
                role="option"
                aria-selected={active}
                disabled={item.disabled}
                onClick={() => pick(item)}
                className={
                  optionClassName ||
                  [
                    "flex w-full items-center justify-between px-4 py-4 text-left text-lg font-semibold",
                    active ? "bg-teal-950 text-white" : "bg-slate-950 text-slate-100",
                    item.disabled ? "opacity-35" : "hover:bg-slate-900 active:bg-slate-800",
                  ].join(" ")
                }
              >
                <span className="min-w-0 truncate">{item.label}</span>
                <span className={active ? "text-teal-300" : "text-slate-700"}>●</span>
              </button>
            );
          })}
        </div>
      ) : null}
    </div>
  );
}
